import { create } from 'zustand';
import { api } from './api';
import { getIdToken, signOut as cognitoSignOut } from './auth';
import { isCustomDomain, rememberTenantSlug, resolveTenantSlug } from './config';
import type { AppUser, Membership, PublicTenant } from './types';

/**
 * Session state: who is signed in, which business the page is for, and what
 * role they hold there. Everything else is fetched per screen.
 */
interface SessionState {
  status: 'loading' | 'ready';
  user: AppUser | null;
  memberships: Membership[];
  /** The business this page is for, resolved from the subdomain or /b/:slug. */
  tenant: PublicTenant | null;
  tenantError: string | null;
  load: () => Promise<void>;
  refreshMemberships: () => Promise<void>;
  /** See the implementation: this is what keeps a new sitter off the home page. */
  adoptMembership: (membership: Membership, tenant?: PublicTenant) => void;
  signOut: () => void;
}

/** The caller's membership in the currently-viewed business, if any. */
export function activeMembership(state: SessionState): Membership | null {
  if (!state.tenant) return null;
  return state.memberships.find((m) => m.tenantId === state.tenant!.tenantId) ?? null;
}

export const useSession = create<SessionState>((set, get) => ({
  status: 'loading',
  user: null,
  memberships: [],
  tenant: null,
  tenantError: null,

  load: async () => {
    // A sitter on their own hostname has no slug to read out of it, so the
    // business is looked up by the host instead. Either way the storefront and
    // the signed-in session load independently: a visitor with no account still
    // needs the business to render.
    const customHost = isCustomDomain() ? window.location.hostname : null;
    const slug = customHost ? null : resolveTenantSlug();
    const lookup = customHost
      ? `/public/domains/${encodeURIComponent(customHost)}`
      : slug
        ? `/public/tenants/${slug}`
        : null;

    const tenantPromise = lookup
      ? api<{ tenant: PublicTenant }>(lookup, { anonymous: true })
          .then((data) => {
            if (slug) rememberTenantSlug(slug);
            return data.tenant;
          })
          .catch(() => null)
      : Promise.resolve(null);

    const sessionPromise = getIdToken().then(async (token) => {
      if (!token) return { user: null, memberships: [] as Membership[] };
      try {
        return await api<{ user: AppUser; memberships: Membership[] }>('/me/memberships');
      } catch {
        // A verified identity with no profile row yet: bootstrap, then retry.
        try {
          await api('/auth/bootstrap', { method: 'POST', body: {} });
          return await api<{ user: AppUser; memberships: Membership[] }>('/me/memberships');
        } catch {
          return { user: null, memberships: [] as Membership[] };
        }
      }
    });

    const [tenant, session] = await Promise.all([tenantPromise, sessionPromise]);

    set({
      status: 'ready',
      tenant,
      tenantError:
        lookup && !tenant ? `No business found at "${customHost ?? slug}".` : null,
      user: session.user,
      memberships: session.memberships,
    });
  },

  /**
   * Take a membership the server has just handed us at face value, rather than
   * re-reading it.
   *
   * The membership list comes from a global secondary index, which is
   * eventually consistent: for a moment after a business is created or a client
   * registers, that index can legitimately answer without the row that was just
   * written. Loading the session again at exactly that moment finds no
   * membership, and the route guard reads "no membership" as "not allowed here"
   * and sends the person to the home page. The response to the write is
   * authoritative and needs no index, so it is what we trust.
   */
  adoptMembership: (membership, tenant) =>
    set((state) => ({
      memberships: [
        ...state.memberships.filter((m) => m.tenantId !== membership.tenantId),
        membership,
      ],
      tenant: tenant ?? state.tenant,
      tenantError: tenant ? null : state.tenantError,
    })),

  refreshMemberships: async () => {
    if (!get().user) return;
    try {
      const session = await api<{ user: AppUser; memberships: Membership[] }>('/me/memberships');
      set({ user: session.user, memberships: session.memberships });
    } catch {
      // Leave the last known good state rather than blanking the session.
    }
  },

  signOut: () => {
    cognitoSignOut();
    set({ user: null, memberships: [] });
  },
}));
